"use client";

import { useEffect, useId, useState, type FormEvent } from "react";
import { RouteFeedbackHistory } from "./PersonalPreferencesPanel";
import { trackRouteOutcome } from "@/lib/planner/analytics-events";
import { readSavedRoute, writeSavedRoute, type SavedRoute } from "@/lib/planner/saved-route";
import {
  buildRouteOutcome, clearRouteOutcomes, explicitRouteElapsedMinutes, exportRouteOutcomes,
  routeJourneyId, ROUTE_OUTCOME_REASONS, ROUTE_OUTCOMES_CHANGED, storeRouteOutcome,
  type RouteAgainAnswer, type RouteOutcomeReason, type RouteTimingSource,
} from "@/lib/planner/route-outcomes";

const button = "min-h-[44px] rounded-lg border border-border-light bg-white px-3 py-2 text-sm font-semibold text-navy disabled:opacity-50 aria-pressed:border-navy aria-pressed:bg-navy aria-pressed:text-white";
type Props = { saved: SavedRoute; onSaved?: (saved: SavedRoute) => void };

export function RouteFeedbackPanel(props: Props) {
  return props.saved.status === "completed" ? <RouteFeedbackForm key={routeJourneyId(props.saved)} {...props} /> : null;
}

function RouteFeedbackForm({ saved, onSaved }: Props) {
  const [answer, setAnswer] = useState<RouteAgainAnswer | null>(saved.outcome?.wouldRideAgain ?? null);
  const [reasons, setReasons] = useState<RouteOutcomeReason[]>(saved.outcome?.reasons ?? []);
  const [timingSource, setTimingSource] = useState<RouteTimingSource>(saved.outcome?.timingSource ?? "none");
  const [minutes, setMinutes] = useState(saved.outcome?.timingSource === "rider-reported" ? String(saved.outcome.actualMinutes) : "");
  const [message, setMessage] = useState<string | null>(null);
  const elapsed = explicitRouteElapsedMinutes(saved);
  const id = useId();
  useEffect(() => {
    const refreshClearedFeedback = () => {
      const current = readSavedRoute();
      if (!current || routeJourneyId(current) !== routeJourneyId(saved) || current.outcome) return;
      setAnswer(null); setReasons([]); setTimingSource("none"); setMinutes("");
    };
    window.addEventListener(ROUTE_OUTCOMES_CHANGED, refreshClearedFeedback);
    return () => window.removeEventListener(ROUTE_OUTCOMES_CHANGED, refreshClearedFeedback);
  }, [saved]);
  const currentRoute = () => {
    const current = readSavedRoute();
    if (!current || current.status !== "completed" || routeJourneyId(current) !== routeJourneyId(saved)) {
      setMessage("Your saved route has changed. Open the completed route before saving its feedback.");
      return null;
    }
    return current;
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!answer) return;
    const current = currentRoute();
    if (!current) return;
    const outcome = buildRouteOutcome(current, {
      wouldRideAgain: answer, reasons,
      timing: timingSource === "none" ? undefined : timingSource === "rider-reported" ? { source: timingSource, minutes: Number(minutes) } : { source: timingSource },
    });
    if (!outcome) { setMessage("Check the journey time. Use 1 to 1,440 minutes for this saved route, including stops."); return; }
    const previous = current.outcome;
    if (previous && previous.wouldRideAgain === outcome.wouldRideAgain
      && [...previous.reasons].sort().join() === [...outcome.reasons].sort().join()
      && previous.actualMinutes === outcome.actualMinutes && previous.timingSource === outcome.timingSource) {
      const archived = storeRouteOutcome(previous);
      setMessage(archived ? "Your feedback is already saved." : "Your feedback is saved with this route, but device storage could not keep it in your feedback history.");
      return;
    }
    const next: SavedRoute = { ...current, outcome };
    if (!writeSavedRoute(next)) { setMessage("Your feedback could not be saved on this device. Please try again."); return; }
    const archived = storeRouteOutcome(outcome);
    trackRouteOutcome(outcome);
    onSaved?.(next);
    setMessage(archived ? "Thanks. Your feedback is saved on this device." : "Thanks. Your feedback is saved with this route, but device storage could not keep it in your feedback history.");
  };

  const download = () => {
    const url = URL.createObjectURL(new Blob([exportRouteOutcomes()], { type: "application/json" }));
    const anchor = document.createElement("a");
    anchor.href = url; anchor.download = "peakwave-route-feedback.json";
    document.body.append(anchor); anchor.click(); anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
    setMessage("Downloaded your saved route feedback. It contains no route coordinates or GPS recordings.");
  };
  const forget = () => {
    const current = currentRoute();
    if (!current) return;
    const next = { ...current, outcome: undefined, feedback: undefined };
    if (!clearRouteOutcomes() || !writeSavedRoute(next)) { setMessage("Some feedback could not be cleared from this device. Please try again."); return; }
    setAnswer(null); setReasons([]); setTimingSource("none"); setMinutes(""); onSaved?.(next);
    setMessage("Your route feedback has been cleared from this device.");
  };

  return <div className="mt-4 border-t border-border-light pt-4">
    <form onSubmit={submit}>
      <fieldset>
        <legend className="font-serif text-xl text-navy">Would you choose this route again?</legend>
        <p className="mt-1 text-sm text-text-muted">{saved.journeyScope === "remaining-route" ? "Think about the revised part of your day." : "Think about this saved route."} Your answer is optional.</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {([["yes", "Yes"], ["maybe", "Maybe"], ["no", "No"]] as const).map(([value, label]) => <button key={value} type="button" className={button} aria-pressed={answer === value} onClick={() => setAnswer(value)}>{label}</button>)}
        </div>
      </fieldset>
      <details className="mt-3" open={reasons.length > 0 || timingSource !== "none" || undefined}>
        <summary className="flex min-h-[44px] cursor-pointer items-center text-sm font-semibold text-navy">Add a reason or journey time</summary>
        <fieldset className="mt-2">
          <legend className="text-sm text-text-muted">What stood out? Choose up to three.</legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {ROUTE_OUTCOME_REASONS.filter(([value]) => saved.preferences.sport === "mtb" || value !== "too-much-climbing").map(([value, label]) => <button key={value} type="button" className={button} aria-pressed={reasons.includes(value)} disabled={reasons.length >= 3 && !reasons.includes(value)} onClick={() => setReasons((previous) => previous.includes(value) ? previous.filter((reason) => reason !== value) : [...previous, value])}>{label}</button>)}
          </div>
        </fieldset>
        <label htmlFor={`${id}-timing`} className="mt-4 block text-sm font-semibold text-navy">Journey time, including stops</label>
        <select id={`${id}-timing`} value={timingSource} onChange={(event) => setTimingSource(event.target.value as RouteTimingSource)} className={`${button} mt-1 w-full`}>
          <option value="none">Do not include time</option>
          {elapsed !== null && <option value="explicit-start-completion">Use time from Start to Finish ({elapsed} minutes)</option>}
          <option value="rider-reported">Enter the time myself</option>
        </select>
        {timingSource === "explicit-start-completion" && <p className="mt-1 text-xs text-text-muted">Measured between your button presses, including breaks. {saved.startedProgress !== 0 ? "You started partway through, so this will not be compared with the full route estimate." : "This is elapsed journey time; it is not GPS-verified."}</p>}
        {timingSource === "rider-reported" && <label className="mt-2 block text-sm text-navy">Minutes for this saved route<input inputMode="numeric" type="number" min={1} max={1440} step={1} required value={minutes} onChange={(event) => setMinutes(event.target.value)} className={`${button} mt-1 block w-full`} /></label>}
      </details>
      <button type="submit" disabled={!answer} className={`${button} mt-3`}>Save feedback</button>
      <p className="mt-2 text-xs text-text-muted">Kept on this device. Optional analytics follow your cookie choice. Your ability settings stay as you chose them.</p>
    </form>
    <details className="mt-2">
      <summary className="flex min-h-[44px] cursor-pointer items-center text-sm text-text-muted">Manage my route feedback</summary>
      <p className="text-xs text-text-muted">This device keeps up to 100 responses for a year. Download a copy or clear this device&apos;s feedback history.</p>
      <RouteFeedbackHistory sport={saved.preferences.sport} />
      <div className="mt-2 flex flex-wrap gap-2"><button type="button" className={button} onClick={download}>Download my feedback</button><button type="button" className={button} onClick={forget}>Clear feedback from this device</button></div>
    </details>
    {message && <p role="status" className="mt-3 text-sm text-navy">{message}</p>}
  </div>;
}
